import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../api/client';

const CODE_LENGTH = 6; // backend app.otp.length — shared by both confirm endpoints

/**
 * Reusable 6-digit confirmation dialog.
 *
 * Both the password-change and account-deletion flows email an OTP and then
 * expect a `ConfirmCodeRequest { code }`. This modal collects that code and
 * delegates the actual call to `onConfirm(code)` (which should return a
 * promise); an ApiError thrown there is surfaced inline.
 */
function ConfirmCodeModal({
  title,
  description,
  confirmLabel = 'Confirmar',
  danger = false,
  onConfirm,
  onClose,
}) {
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputsRef = useRef([]);

  const code = useMemo(() => digits.join(''), [digits]);
  const complete = code.length === CODE_LENGTH && !digits.includes('');

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  // Close on Escape for keyboard users.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const setDigit = (index, value) =>
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });

  const handleChange = (index) => (event) => {
    const raw = event.target.value.replace(/\D/g, '');
    if (error) setError('');

    if (raw.length > 1) {
      const chars = raw.slice(0, CODE_LENGTH - index).split('');
      setDigits((prev) => {
        const next = [...prev];
        chars.forEach((char, i) => {
          next[index + i] = char;
        });
        return next;
      });
      inputsRef.current[Math.min(index + chars.length, CODE_LENGTH - 1)]?.focus();
      return;
    }

    setDigit(index, raw);
    if (raw && index < CODE_LENGTH - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index) => (event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
      setDigit(index - 1, '');
    }
    if (event.key === 'ArrowLeft' && index > 0) inputsRef.current[index - 1]?.focus();
    if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1)
      inputsRef.current[index + 1]?.focus();
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    event.preventDefault();
    const next = Array(CODE_LENGTH).fill('');
    pasted
      .slice(0, CODE_LENGTH)
      .split('')
      .forEach((char, i) => {
        next[i] = char;
      });
    setDigits(next);
    inputsRef.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!complete) {
      setError('Introduce los 6 dígitos del código.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onConfirm(code);
      // On success the parent closes the modal / navigates away.
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail || err.message
          : 'No hemos podido verificar el código. Inténtalo de nuevo.'
      );
      setDigits(Array(CODE_LENGTH).fill(''));
      inputsRef.current[0]?.focus();
      setSubmitting(false);
    }
  };

  return (
    <div className="Modal" role="presentation" onMouseDown={onClose}>
      <div
        className="Modal__card"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="Modal__close"
          onClick={onClose}
          aria-label="Cerrar"
          disabled={submitting}
        >
          ×
        </button>

        <h3 className="Modal__title">{title}</h3>
        <p className="Modal__desc">{description}</p>

        <form onSubmit={handleSubmit} noValidate>
          <div className="CodeCells" onPaste={handlePaste}>
            {digits.map((digit, index) => (
              <input
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                ref={(el) => {
                  inputsRef.current[index] = el;
                }}
                className={`CodeCell${error ? ' CodeCell--invalid' : ''}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                aria-label={`Dígito ${index + 1}`}
                value={digit}
                onChange={handleChange(index)}
                onKeyDown={handleKeyDown(index)}
              />
            ))}
          </div>

          {error && <p className="Modal__error">{error}</p>}

          <div className="Modal__actions">
            <button
              type="button"
              className="Btn Btn--ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={`Btn ${danger ? 'Btn--danger' : 'Btn--primary'}`}
              disabled={submitting || !complete}
            >
              {submitting ? 'Verificando…' : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ConfirmCodeModal;
